// ============================================================================
// REMINDER MODAL v2 — crear y editar recordatorios
// ============================================================================
import { useState, useEffect } from 'react';
import { Bell, X, Loader2, AlertCircle } from 'lucide-react';
import { remindersApi, type ReminderDto } from '../../api/reminders';
import { reminderTemplatesApi, type ReminderTemplateForSite } from '../../api/reminder-templates';
import { Modal, ModalFooter } from '../../components/ui/Modal';
import { useToast } from '../../components/ui/Toast';

// Variables disponibles
export const TEMPLATE_VARS = [
  { key: '{{nombre}}',      label: 'Nombre del paciente' },
  { key: '{{fecha}}',       label: 'Fecha del recordatorio' },
  { key: '{{hora}}',        label: 'Hora de envío' },
  { key: '{{tratamiento}}', label: 'Nombre del tratamiento' },
  { key: '{{presupuesto}}', label: 'Nº de presupuesto' },
  { key: '{{deuda}}',       label: 'Deuda pendiente en €' },
];

/**
 * Detecta si una plantilla requiere datos de presupuesto.
 * Una plantilla requiere budget si contiene {{presupuesto}}, {{tratamiento}} o {{deuda}}.
 */
function templateRequiresBudget(template: string): boolean {
  return /\{\{(presupuesto|tratamiento|deuda)\}\}/i.test(template);
}

function resolvePreview(template: string, patientName: string, date: string, vars?: {
  tratamiento?: string; presupuesto?: string; deuda?: string;
}): string {
  const dateFormatted = date
    ? new Date(date + 'T00:00:00').toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })
    : '';
  return template
    .replace(/\{\{nombre\}\}/gi,       patientName || 'Paciente')
    .replace(/\{\{fecha\}\}/gi,        dateFormatted)
    .replace(/\{\{hora\}\}/gi,         '10:00')
    .replace(/\{\{tratamiento\}\}/gi,  vars?.tratamiento || 'Ortodoncia')
    .replace(/\{\{presupuesto\}\}/gi,  vars?.presupuesto || 'MLG-2026-0001')
    .replace(/\{\{deuda\}\}/gi,        vars?.deuda || '450,00€');
}

interface ReminderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved?: () => void;
  patientId: string;
  patientName: string;
  budgetId?: string;
  budgetNumber?: string;
  treatmentName?: string;
  pendingAmount?: number | null;
  reminderToEdit?: ReminderDto; // si viene → modo edición
}

export function ReminderModal({
  isOpen, onClose, onSaved,
  patientId, patientName, budgetId, budgetNumber,
  treatmentName, pendingAmount,
  reminderToEdit,
}: ReminderModalProps) {
  const toast = useToast();
  const isEdit = !!reminderToEdit;
  const [templates, setTemplates] = useState<ReminderTemplateForSite[]>([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [templateId, setTemplateId] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [scheduledDate, setScheduledDate] = useState('');
  const [useCustomMessage, setUseCustomMessage] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const todayStr = new Date().toISOString().split('T')[0];
  const selectedTemplate = templates.find(t => t.id === templateId);

  useEffect(() => {
    if (!isOpen) return;
    setError(null);

    // Pre-rellenar si es edición
    if (reminderToEdit) {
      setScheduledDate(reminderToEdit.scheduledDate);
      if (reminderToEdit.customMessage) {
        setUseCustomMessage(true);
        setCustomMessage(reminderToEdit.customMessage);
      } else {
        setUseCustomMessage(false);
        setCustomMessage('');
      }
    } else {
      setScheduledDate('');
      setUseCustomMessage(false);
      setCustomMessage('');
    }

    setIsLoadingTemplates(true);
    reminderTemplatesApi.getForSite()
      .then(data => {
        setTemplates(data);
        if (reminderToEdit?.templateId) {
          setTemplateId(reminderToEdit.templateId);
        } else if (data.length > 0) {
          // Si no hay budget, elegir la primera plantilla compatible
          if (!budgetId) {
            const compatible = data.find(t => !templateRequiresBudget(t.messageTemplate));
            setTemplateId((compatible || data[0]).id);
          } else {
            setTemplateId(data[0].id);
          }
        }
      })
      .catch(() => setTemplates([]))
      .finally(() => setIsLoadingTemplates(false));
  }, [isOpen]);

  const handleSave = async () => {
    if (!scheduledDate) { setError('La fecha es obligatoria'); return; }
    if (!useCustomMessage && !templateId) { setError('Selecciona una plantilla o escribe un mensaje personalizado'); return; }
    if (useCustomMessage && !customMessage.trim()) { setError('El mensaje no puede estar vacío'); return; }

    // Validación defensiva: plantilla incompatible sin budget
    if (!useCustomMessage && templateId && !budgetId) {
      const tpl = templates.find(t => t.id === templateId);
      if (tpl && templateRequiresBudget(tpl.messageTemplate)) {
        setError('La plantilla seleccionada requiere un presupuesto asociado. Elige otra o usa mensaje personalizado.');
        return;
      }
    }

    setIsSaving(true); setError(null);
    try {
      if (isEdit && reminderToEdit) {
        await remindersApi.update(reminderToEdit.id, {
          scheduledDate,
          templateId: !useCustomMessage ? templateId : undefined,
          customMessage: useCustomMessage ? customMessage.trim() : undefined,
        });
        toast.success('Actualizado', 'Recordatorio modificado correctamente');
      } else {
        await remindersApi.create({
          patientId,
          budgetId: budgetId || undefined,
          templateId: !useCustomMessage ? templateId : undefined,
          customMessage: useCustomMessage ? customMessage.trim() : undefined,
          scheduledDate,
        });
        toast.success('Programado', `Recordatorio programado para el ${new Date(scheduledDate + 'T00:00:00').toLocaleDateString('es-ES')}`);
      }
      onSaved?.();
      onClose();
    } catch (err: any) {
      setError(err.message || 'No se pudo guardar el recordatorio');
    } finally { setIsSaving(false); }
  };

  const messageForPreview = useCustomMessage
    ? customMessage
    : (selectedTemplate?.messageTemplate || '');

  const previewVars = {
    tratamiento: treatmentName || undefined,
    presupuesto: budgetNumber || undefined,
    deuda: pendingAmount != null && pendingAmount > 0
      ? pendingAmount.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '€'
      : undefined,
  };

  return (
    <Modal isOpen={isOpen} onClose={() => !isSaving && onClose()}
      title={isEdit ? 'Editar recordatorio' : 'Programar recordatorio'}
      subtitle={`Paciente: ${patientName}${budgetNumber ? ` · Presupuesto ${budgetNumber}` : ''}`}
      size="md">
      <div className="space-y-4">
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
            <AlertCircle className="h-4 w-4 text-red-600 shrink-0 mt-0.5" />
            <p className="text-sm text-red-700 flex-1">{error}</p>
            <button onClick={() => setError(null)} className="p-1 text-red-400 hover:text-red-600"><X className="h-4 w-4" /></button>
          </div>
        )}

        {/* Fecha */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Fecha del recordatorio *</label>
          <input type="date" value={scheduledDate} min={todayStr}
            onChange={e => { setScheduledDate(e.target.value); setError(null); }}
            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#008DD0]" />
        </div>

        {/* Mensaje */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium text-gray-700">Mensaje</label>
            <label className="flex items-center gap-2 text-xs text-gray-500 cursor-pointer">
              <input type="checkbox" checked={useCustomMessage} onChange={e => setUseCustomMessage(e.target.checked)}
                className="rounded border-gray-300 text-blue-600" />
              Mensaje personalizado
            </label>
          </div>

          {!useCustomMessage ? (
            isLoadingTemplates ? (
              <div className="flex items-center gap-2 py-3 text-sm text-gray-400">
                <Loader2 className="h-4 w-4 animate-spin" /> Cargando plantillas...
              </div>
            ) : templates.length === 0 ? (
              <p className="text-sm text-amber-600 p-3 bg-amber-50 rounded-xl border border-amber-100">
                No hay plantillas asignadas a este centro.
              </p>
            ) : (
              <>
                <select value={templateId} onChange={e => { setTemplateId(e.target.value); setError(null); }}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#008DD0]">
                  {templates.map(t => {
                    const requiresBudget = templateRequiresBudget(t.messageTemplate);
                    const disabled = requiresBudget && !budgetId;
                    return (
                      <option
                        key={t.id}
                        value={t.id}
                        disabled={disabled}
                        title={disabled ? 'Esta plantilla requiere un presupuesto asociado' : undefined}
                      >
                        {t.name}{disabled ? ' — requiere presupuesto' : ''}
                      </option>
                    );
                  })}
                </select>
                {!budgetId && templates.length > 0 && templates.every(t => templateRequiresBudget(t.messageTemplate)) && (
                  <p className="text-xs text-amber-600 mt-1.5">
                    Ninguna plantilla es compatible sin presupuesto asociado. Usa mensaje personalizado.
                  </p>
                )}
              </>
            )
          ) : (
            <textarea value={customMessage} onChange={e => { setCustomMessage(e.target.value); setError(null); }}
              rows={3} maxLength={1000}
              placeholder="Escribe el mensaje... Puedes usar {{nombre}}, {{fecha}}, {{tratamiento}}, {{presupuesto}}, {{deuda}}"
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#008DD0] resize-none" />
          )}
        </div>

        {/* Preview */}
        {scheduledDate && messageForPreview && (
          <div>
            <p className="text-xs text-gray-500 mb-1 font-medium">Vista previa:</p>
            <div className="p-3 bg-green-50 border border-green-100 rounded-xl text-sm text-gray-700 whitespace-pre-wrap">
              {resolvePreview(messageForPreview, patientName, scheduledDate, previewVars)}
            </div>
          </div>
        )}
      </div>

      <ModalFooter>
        <button type="button" onClick={onClose} disabled={isSaving}
          className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 disabled:opacity-50 flex items-center gap-2">
          <X className="h-4 w-4" /> Cancelar
        </button>
        <button type="button" onClick={handleSave} disabled={isSaving}
          className="px-4 py-2.5 text-sm font-medium text-white bg-[#008DD0] hover:bg-[#007ab8] rounded-xl disabled:opacity-50 flex items-center gap-2">
          {isSaving
            ? <><Loader2 className="h-4 w-4 animate-spin" />Guardando...</>
            : <><Bell className="h-4 w-4" />{isEdit ? 'Guardar cambios' : 'Programar'}</>}
        </button>
      </ModalFooter>
    </Modal>
  );
}

export default ReminderModal;
